<template>
  <div
    class="relative w-full flex-col items-center text-white bg-[#120F14] mt-[70px] pb-[80px]"
  >
    <!-- Scrollable Content Wrapper -->
    <div class="w-full h-full flex flex-col items-center justify-start">
      <!-- Raffle Hub Banner Section -->
      <section class="relative w-full max-w-[1400px] mx-auto px-4 lg:px-8 xl:px-10 pt-6">
        <div class="relative w-full min-h-[180px] sm:min-h-[200px] overflow-hidden rounded-[16px] flex flex-col md:flex-row items-center justify-between p-6 sm:p-8 xl:p-10 gap-6">
          <!-- Background Image -->
          <div class="absolute inset-0 bg-[#14181F] border border-white/5 rounded-[16px] pointer-events-none z-0"></div>
          <div class="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent z-0 pointer-events-none"></div>
          
          <!-- Text Content -->
          <div class="relative z-10 flex flex-col max-w-xl">
            <h1 class="text-3xl md:text-4xl lg:text-[42px] font-black tracking-wider uppercase font-sans text-white leading-none mb-2">
              {{ $t('Raffle.RaffleHub') }}
            </h1>
            <p class="text-[#B9CACB] text-sm md:text-base leading-relaxed">
              {{ $t('Raffle.RaffleDesc') }}
            </p>
          </div>

          <!-- Tickets Counter -->
          <div class="relative z-10 bg-[#161c28]/90 backdrop-blur-md border border-[#00F0FF]/30 rounded-[12px] p-4 flex items-center gap-4 min-w-[220px]">
             <!-- left cyan edge line -->
             <div class="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-3/4 bg-[#00F0FF] rounded-r-md"></div>
             
             <!-- Ticket Icon -->
             <div class="w-12 h-12 bg-[#0D0B10] rounded-lg flex items-center justify-center border border-white/5 ml-2">
               <img src="@/assets/revamp/icons/ticket.png" alt="Ticket" class="w-6 h-6 object-contain" />
             </div>
             <!-- Count -->
             <div class="flex flex-col">
               <span class="text-white/60 text-[10px] font-bold uppercase tracking-wider">{{ $t('RafflesPage.AvailableTickets') }}</span>
               <div class="flex items-center">
                 <div v-if="loading || animatedPoints === null" class="w-4 h-4 rounded-full border-2 border-[#00F0FF] border-t-transparent animate-spin mt-1"></div>
                 <span v-else class="text-2xl font-black text-[#00F0FF] font-sans leading-none mt-1">{{ animatedPoints }}</span>
               </div>
             </div>
          </div>
        </div>

        <!-- How It Works Button -->
        <div class="flex justify-end w-full mt-3 pr-2">
          <button
            @click="showHowItWorksModal = true"
            class="flex items-center gap-2 text-sm text-[#00F0FF] font-semibold transition hover:opacity-80"
          >
            <div
              class="w-4 h-4 flex items-center justify-center rounded-full border border-[#00F0FF] text-[#00F0FF] text-[10px] font-bold mr-1"
            >
              !
            </div>
            {{ $t('Raffle.HowItWorks') }}
          </button>
        </div>
      </section>

      <!-- Raffles Container -->
      <div
        class="flex flex-col justify-center items-center w-full max-w-[1200px] pb-[50px] mx-auto mt-10 focus:outline-none"
      >
        <!-- Show spinner if loading -->
        <div v-if="loading" class="flex justify-center items-center min-h-[300px] w-full">
          <div class="twist-spinner"></div>
        </div>

        <div v-else class="flex flex-col justify-center items-center w-full px-6">
          <div class="relative z-20 w-full max-w-6xl mt-10 rounded-lg">
            <div
              class="hidden lg:flex flex-col xl:flex-row items-start lg:items-center justify-between gap-4 lg:gap-6 select-none"
            >
              <!-- Category Buttons: All, Tips, Bonus -->
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="category in ['All', 'Current', 'Ended']"
                  :key="category"
                  @click="selectedCategory = category"
                  class="px-6 py-4 text-sm font-bold rounded-md transition-all"
                  :class="{
                    'bg-[#00F0FF] text-black': selectedCategory === category,
                    'bg-[#0A0A0A] text-white hover:bg-[#00F0FF]/20':
                      selectedCategory !== category,
                  }"
                >
                {{ $t('Category.Raffle.' + category) }}
                </button>
              </div>

              <!-- Controls Row -->
              <div class="flex flex-wrap items-center gap-3 justify-end w-full lg:w-auto">
                <!-- Search Bar -->
                <div
                  class="relative flex items-center bg-transparent border border-[#00F0FF] rounded-lg px-4 h-[52px] min-w-[260px]"
                >
                  <svg
                    class="w-5 h-5 text-[#00F0FF] mr-2"
                    fill="none"
                    stroke="#00F0FF"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M21 21l-4.35-4.35m2.35-6.65a7 7 0 1 1-14 0 7 7 0 0 1 14 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    v-model="searchQuery"
                    :placeholder="$t('Store.SearchPlaceholder')"
                    class="bg-transparent text-sm text-[#00F0FF] placeholder-[#00F0FF] focus:outline-none w-full"
                  />
                </div>

                <!-- Sort Dropdown -->
                <div class="relative h-[52px]">
                  <div
                    @click="toggleSortDropdown"
                    class="flex items-center justify-between h-full bg-transparent text-sm min-w-[200px] border border-[#00F0FF] rounded-lg px-4 py-4 cursor-pointer"
                  >
                    <span class="text-[#00F0FF]">{{ selectedSortOption.label }}</span>
                    <svg
                      class="w-4 h-4 text-[#00F0FF] ml-2 transition-transform"
                      :class="{ 'rotate-180': showSortDropdown }"
                      fill="none"
                      stroke="#00F0FF"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  <transition name="fade">
                    <div
                      v-if="showSortDropdown"
                      class="absolute top-14 left-0 bg-[#1D1D1D] border border-[#00F0FF] rounded-lg shadow-lg w-full z-50"
                    >
                      <div
                        v-for="option in sortOptions"
                        :key="option.value"
                        @click="applySort(option)"
                        class="px-4 py-3 hover:bg-[#302f2f] cursor-pointer transition-all"
                        :class="{
                          'text-[#00F0FF] bg-[#302f2f]':
                            option.value === selectedSortOption.value,
                          'text-[#B0B0B0]': option.value !== selectedSortOption.value,
                        }"
                      >
                        {{ option.label }}
                      </div>
                    </div>
                  </transition>
                </div>

                <!-- Raffle History + Refresh -->
                <div class="flex items-center gap-2">
                  <button
                    @click="fetchTransactions"
                    class="h-[52px] px-6 py-3 bg-[#00F0FF] text-black font-bold text-sm rounded-lg hover:opacity-90"
                  >
                  {{ $t("Raffle.Raffle_History") }}
                  </button>
                  <button
                    @click="refreshPage"
                    class="h-[52px] w-[52px] bg-[#00F0FF] rounded-lg flex items-center justify-center hover:opacity-90"
                    title="Refresh"
                  >
                    <img src="@/assets/refresh.png" alt="Refresh" class="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Disclaimer Search Bar Mobile -->
          <div class="flex lg:hidden justify-center  gap-3 w-full mb-4 px-4">
            <button
              v-for="category in ['All', 'Current', 'Ended']"
              :key="category"
              @click="selectedCategory = category"
              class="px-6 py-3 text-sm font-bold rounded-md transition-all"
              :class="{
                'bg-[#00F0FF] text-black': selectedCategory === category,
                'bg-[#0A0A0A] text-white hover:bg-[#00F0FF]/20':
                  selectedCategory !== category,
              }"
            >
              {{ category }}
            </button>
          </div>

          <div
            class="relative flex lg:hidden items-center bg-transparent w-full border border-[#00F0FF] rounded-xl px-4 py-4 mt-10"
          >
            <!-- Search Icon (SVG) -->
            <svg
              class="w-5 h-5 text-[#00F0FF] mr-2"
              fill="none"
              stroke="#00F0FF"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M21 21l-4.35-4.35m2.35-6.65a7 7 0 1 1-14 0 7 7 0 0 1 14 0z"
              />
            </svg>
            <!-- Input Field -->
            <input
              type="text"
              v-model="searchQuery"
              :placeholder="$t('Store.SearchPlaceholder')"
              class="bg-transparent text-[#00F0FF] placeholder-[#00F0FF] focus:outline-none w-full"
            />
          </div>

          <div
            class="relative z-30 flex lg:hidden justify-between items-center w-full mt-6"
          >
            <!-- Sort Dropdown -->
            <div class="relative w-full">
              <div
                @click="toggleSortDropdown"
                class="flex items-center justify-between bg-transparent lg:text-sm xl:text-base w-full lg:w-[200px] xl:w-[260px] border border-[#00F0FF] rounded-xl px-4 py-4 cursor-pointer select-none"
              >
                <span class="text-[#00F0FF]">{{ selectedSortOption.label }}</span>
                <!-- Dropdown Arrow -->
                <svg
                  class="w-4 h-4 text-[#00F0FF] ml-6 transition-transform"
                  :class="{ 'rotate-180': showSortDropdown }"
                  fill="none"
                  stroke="#00F0FF"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              <!-- Sort Options Dropdown -->
              <transition name="fade">
                <div
                  v-if="showSortDropdown"
                  class="absolute top-14 left-0 bg-[#1D1D1D] border border-[#00F0FF] rounded-xl shadow-lg w-full z-50"
                >
                  <div
                    v-for="option in sortOptions"
                    :key="option.value"
                    @click="applySort(option)"
                    class="px-4 py-3 hover:bg-[#302f2f] cursor-pointer transition-all rounded-b-xl"
                    :class="{
                      'text-[#00F0FF] bg-[#302f2f] rounded-t-xl':
                        option.label === selectedSortOption,
                      'text-[#B0B0B0]': option.label !== selectedSortOption,
                    }"
                  >
                    {{ option.label }}
                  </div>
                </div>
              </transition>
            </div>
            <div class="hidden sm:flex justify-end items-center gap-x-2 w-full">
              <!-- Purchase History Button -->
              <button
                @click="fetchTransactions"
                class="bg-[#00F0FF] text-black px-6 py-4 rounded-lg font-semibold hover:opacity-90"
              >
                {{ $t("Store.PurchaseHistory") }}
              </button>
              <button
                @click="refreshPage"
                class="h-[52px] w-[52px] bg-[#00F0FF] rounded-lg flex items-center justify-center hover:opacity-90"
                title="Refresh"
              >
                <img src="@/assets/refresh.png" alt="Refresh" class="w-5 h-5" />
              </button>
            </div>
          </div>

          <div class="flex sm:hidden justify-end items-center gap-x-2 w-full mt-10">
            <!-- Purchase History Button -->
            <button
              @click="fetchTransactions"
              class="bg-[#00F0FF] text-black px-6 py-4 rounded-lg font-semibold hover:opacity-90"
            >
              {{ $t("Store.PurchaseHistory") }}
            </button>
            <button
              @click="refreshPage"
              class="h-[52px] w-[52px] bg-[#00F0FF] rounded-lg flex items-center justify-center hover:opacity-90"
              title="Refresh"
            >
              <img src="@/assets/refresh.png" alt="Refresh" class="w-5 h-5" />
            </button>
          </div>

          <!-- WRAPPER -->
          <div class="w-full max-w-7xl mt-10">
            <!-- CURRENT RAFFLES -->
            <div v-if="['All', 'Current'].includes(selectedCategory)" class="mb-16">
              <h2
                class="text-white text-center md:text-start text-lg sm:text-xl font-bold mb-4"
              >
              {{ $t("Raffle.Current_Raffles") }}
              </h2>
              <div
                class="grid gap-4 gap-y-10 sm:gap-6 mt-8 place-items-start"
                style="grid-template-columns: repeat(auto-fill, minmax(250px, 1fr))"
              >
                <div
                  v-for="item in activePaginatedRaffles"
                  :key="'current-' + item._id"
                  class="bg-[#14181F] border border-gray-800 rounded-[16px] overflow-hidden flex flex-col w-full min-h-[380px]"
                >
                  <!-- Image Section -->
                  <div class="relative w-full h-[180px] bg-[#0D0B10] flex items-center justify-center p-4">
                    <img
                      :src="item.imageUrl || require('@/assets/store-fallback.png')"
                      alt="Gift"
                      class="max-w-full max-h-full object-contain"
                    />
                  </div>

                  <!-- Content Section -->
                  <div class="p-6 flex flex-col flex-grow">
                    <!-- Title -->
                    <h3 class="text-white text-lg sm:text-xl font-bold font-sans mb-4 min-h-[56px] line-clamp-2" :title="item.name">
                      {{ item.name }}
                    </h3>

                    <!-- Stats Row -->
                    <div class="flex justify-between items-center mb-6 bg-[#0D0B10] rounded-xl p-4 border border-gray-800/50">
                      <div class="flex flex-col text-center w-1/2 border-r border-gray-800/50">
                        <span class="text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-1">{{ $t('Raffle.Entries') }}</span>
                        <span class="text-white font-black text-lg">{{ (item.entries || 0).toLocaleString() }}</span>
                      </div>
                      <div class="flex flex-col text-center w-1/2">
                        <span class="text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-1">{{ $t('Raffle.EndsIn') }}</span>
                        <span class="text-white font-black text-lg">{{ countdowns[item._id] || "..." }}</span>
                      </div>
                    </div>

                    <!-- Button -->
                    <button
                      @click="goToRaffle(item._id)"
                      class="w-full py-4 bg-[#00F0FF] text-black font-black uppercase tracking-wider text-sm rounded-[10px] hover:bg-[#00d4e0] transition-colors mt-auto flex items-center justify-center gap-2"
                    >
                      {{ $t('Raffle.EnterFor') }} 
                      <img src="@/assets/coin.png" alt="Coin" class="w-4 h-4" />
                      {{ item.entryCost }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Current Raffles Pagination -->
            <div
              v-if="['All', 'Current'].includes(selectedCategory)"
              class="mt-10 mb-10 lg:mt-[60px] w-full py-[16px] lg:bg-transparent lg:backdrop-blur-sm"
            >
              <div class="hidden lg:block w-full">
                <div class="p-[1px] bg-gradient-to-r from-[#00F0FF] to-[#A144F6] rounded-[10px]">
                  <div class="bg-[#14181F] rounded-[9px] flex justify-between items-center w-full px-6 h-[70px]">
                    <!-- Left: Items Count -->
                    <p class="text-white font-bold text-sm sm:text-base font-sans">
                      {{ $t('Store.Showing') }}
                      {{
                        activePaginatedRaffles.length
                          ? (currentActivePage - 1) * itemsPerPage + 1
                          : 0
                      }}
                      -
                      {{ Math.min(currentActivePage * itemsPerPage, activeTotalItems) }} {{ $t('Store.Of') }}
                      {{ activeTotalItems }} {{ $t('Store.Items') }}
                    </p>
                    
                    <!-- Right: Pagination Buttons -->
                    <div class="flex items-center gap-2">
                      <!-- Previous Button -->
                      <button
                        @click="prevActivePage"
                        :disabled="currentActivePage === 1"
                        class="flex items-center justify-center w-10 h-10 rounded-[8px] bg-[#0D0B10] border border-[#00F0FF]/20 text-white disabled:opacity-50 hover:bg-white/5 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>

                      <!-- Page Numbers -->
                      <span v-for="page in activeTotalPages" :key="'active-page-' + page">
                        <button
                          @click="goToActivePage(page)"
                          :class="[
                            'w-10 h-10 rounded-[8px] font-bold transition-colors text-sm',
                            currentPage === page
                              ? 'bg-[#00F0FF] text-black border border-[#00F0FF]'
                              : 'bg-[#0D0B10] border border-[#00F0FF]/20 text-white hover:bg-white/5',
                          ]"
                        >
                          {{ page }}
                        </button>
                      </span>

                      <!-- Next Button -->
                      <button
                        @click="nextActivePage"
                        :disabled="currentActivePage === activeTotalPages"
                        class="flex items-center justify-center w-10 h-10 rounded-[8px] bg-[#0D0B10] border border-[#00F0FF]/20 text-white disabled:opacity-50 hover:bg-white/5 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Mobile Layout (Hidden on Desktop) -->
              <div class="flex flex-col items-center mt-3 lg:hidden w-full">
                <p class="text-white font-bold text-xl mb-8">
                  {{ $t('Store.Showing') }}
                  {{
                    activePaginatedRaffles.length
                      ? (currentActivePage - 1) * itemsPerPage + 1
                      : 0
                  }}
                  - {{ Math.min(currentActivePage * itemsPerPage, activeTotalItems) }} {{ $t('Store.Of') }}
                  {{ activeTotalItems }} {{ $t('Store.Items') }}
                </p>

                <div class="flex gap-2">
                  <!-- Previous Button -->
                  <button
                    @click="prevActivePage"
                    :disabled="currentActivePage === 1"
                    class="flex items-center justify-center w-12 h-12 rounded-md bg-black border border-[#00F0FF] text-white disabled:opacity-50"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  <!-- Page Numbers -->
                  <span v-for="page in activeTotalPages" :key="'active-page-' + page">
                    <button
                      @click="goToActivePage(page)"
                      :class="[
                        'w-12 h-12 rounded-md font-bold',
                        currentPage === page
                          ? 'bg-[#00F0FF] text-white'
                          : 'bg-black border border-[#00F0FF] text-white',
                      ]"
                    >
                      {{ page }}
                    </button>
                  </span>

                  <!-- Next Button -->
                  <button
                    @click="nextActivePage"
                    :disabled="currentActivePage === activeTotalPages"
                    class="flex items-center justify-center w-12 h-12 rounded-md bg-black border border-[#00F0FF] text-white disabled:opacity-50"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- ENDED RAFFLES -->
            <div v-if="['All', 'Ended'].includes(selectedCategory)">
              <h2
                class="text-white text-center md:text-start text-lg sm:text-xl font-bold mb-4"
              >
              {{ $t("Raffle.Ended_Raffles") }}
              </h2>
              <div
                class="grid gap-4 gap-y-10 sm:gap-6 mt-8 place-items-start"
                style="grid-template-columns: repeat(auto-fill, minmax(250px, 1fr))"
              >
                <div
                  v-for="item in inactivePaginatedRaffles"
                  :key="'ended-' + item._id"
                  class="bg-[#14181F] border border-gray-800 rounded-[16px] overflow-hidden flex flex-col w-full min-h-[380px] opacity-75"
                >
                  <!-- Image Section -->
                  <div class="relative w-full h-[180px] bg-[#0D0B10] flex items-center justify-center p-4">
                    <img
                      :src="item.imageUrl || require('@/assets/store-fallback.png')"
                      alt="Gift"
                      class="max-w-full max-h-full object-contain grayscale"
                    />
                  </div>

                  <!-- Content Section -->
                  <div class="p-6 flex flex-col flex-grow">
                    <!-- Title -->
                    <h3 class="text-white text-lg sm:text-xl font-bold font-sans mb-4 min-h-[56px] line-clamp-2" :title="item.name">
                      {{ item.name }}
                    </h3>

                    <!-- Stats Row -->
                    <div class="flex justify-between items-center mb-6 bg-[#0D0B10] rounded-xl p-4 border border-gray-800/50">
                      <div class="flex flex-col text-center w-1/2 border-r border-gray-800/50">
                        <span class="text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-1">{{ $t('Raffle.Entries') }}</span>
                        <span class="text-white font-black text-lg">{{ (item.entries || 0).toLocaleString() }}</span>
                      </div>
                      <div class="flex flex-col text-center w-1/2">
                        <span class="text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-1">{{ $t('Raffle.Status') }}</span>
                        <span class="text-white font-black text-lg text-[#7F85A4]">{{ $t('Raffle.Ended') }}</span>
                      </div>
                    </div>

                    <!-- Button -->
                    <button
                      @click="goToRaffle(item._id)"
                      class="w-full py-4 bg-[#0D0B10] border border-gray-800 text-[#B9CACB] hover:text-white font-black uppercase tracking-wider text-sm rounded-[10px] transition-colors mt-auto flex items-center justify-center"
                    >
                      {{ $t('Raffle.ViewDetails') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Ended Raffles Pagination -->
          <div
            v-if="['All', 'Ended'].includes(selectedCategory)"
            class="mt-10 lg:mt-[60px] w-full py-[16px] lg:bg-transparent lg:backdrop-blur-sm"
          >
            <div class="hidden lg:block w-full">
              <div class="p-[1px] bg-gradient-to-r from-[#00F0FF] to-[#A144F6] rounded-[10px]">
                <div class="bg-[#14181F] rounded-[9px] flex justify-between items-center w-full px-6 h-[70px]">
                  <!-- Left: Items Count -->
                  <p class="text-white font-bold text-sm sm:text-base font-sans">
                    {{ $t('Store.Showing') }}
                    {{
                      inactivePaginatedRaffles.length
                        ? (currentInactivePage - 1) * itemsPerPage + 1
                        : 0
                    }}
                    -
                    {{ Math.min(currentInactivePage * itemsPerPage, inactiveTotalItems) }} {{ $t('Store.Of') }}
                    {{ inactiveTotalItems }} {{ $t('Store.Items') }}
                  </p>
                  
                  <!-- Right: Pagination Buttons -->
                  <div class="flex items-center gap-2">
                    <!-- Previous Button -->
                    <button
                      @click="prevInactivePage"
                      :disabled="currentInactivePage === 1"
                      class="flex items-center justify-center w-10 h-10 rounded-[8px] bg-[#0D0B10] border border-[#00F0FF]/20 text-white disabled:opacity-50 hover:bg-white/5 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>

                    <!-- Page Numbers -->
                    <span v-for="page in inactiveTotalPages" :key="'ended-page-' + page">
                      <button
                        @click="goToInactivePage(page)"
                        :class="[
                          'w-10 h-10 rounded-[8px] font-bold transition-colors text-sm',
                          currentInactivePage === page
                            ? 'bg-[#00F0FF] text-black border border-[#00F0FF]'
                            : 'bg-[#0D0B10] border border-[#00F0FF]/20 text-white hover:bg-white/5',
                        ]"
                      >
                        {{ page }}
                      </button>
                    </span>

                    <!-- Next Button -->
                    <button
                      @click="nextInactivePage"
                      :disabled="currentInactivePage === inactiveTotalPages"
                      class="flex items-center justify-center w-10 h-10 rounded-[8px] bg-[#0D0B10] border border-[#00F0FF]/20 text-white disabled:opacity-50 hover:bg-white/5 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Mobile Layout (Hidden on Desktop) -->
            <div class="flex flex-col items-center mt-3 lg:hidden w-full">
              <p class="text-white font-bold text-xl mb-8">
                {{ $t('Store.Showing') }}
                {{
                  inactivePaginatedRaffles.length
                    ? (currentInactivePage - 1) * itemsPerPage + 1
                    : 0
                }}
                -
                {{ Math.min(currentInactivePage * itemsPerPage, inactiveTotalItems) }} {{ $t('Store.Of') }}
                {{ inactiveTotalItems }} {{ $t('Store.Items') }}
              </p>

              <div class="flex gap-2">
                <!-- Previous Button -->
                <button
                  @click="prevInactivePage"
                  :disabled="currentInactivePage === 1"
                  class="flex items-center justify-center w-12 h-12 rounded-md bg-black border border-[#00F0FF] text-white disabled:opacity-50"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                <!-- Page Numbers -->
                <span v-for="page in inactiveTotalPages" :key="'ended-page-' + page">
                  <button
                    @click="goToInactivePage(page)"
                    :class="[
                      'w-12 h-12 rounded-md font-bold',
                      currentPage === page
                        ? 'bg-[#00F0FF] text-white'
                        : 'bg-black border border-[#00F0FF] text-white',
                    ]"
                  >
                    {{ page }}
                  </button>
                </span>

                <!-- Next Button -->
                <button
                  @click="nextInactivePage"
                  :disabled="currentInactivePage === inactiveTotalPages"
                  class="flex items-center justify-center w-12 h-12 rounded-md bg-black border border-[#00F0FF] text-white disabled:opacity-50"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <RaffleTransactionModal
    :show="showTransactionModal"
    :transactions="transactions"
    @close="showTransactionModal = false"
  />

  <RedirectModal
    :showModal="showRedirectModal"
    :user="$store.state.user"
    :isUserLoggedIn="isUserLoggedIn"
    @close="showRedirectModal = false"
  />

  <HowRafflesWorkModal :show="showHowItWorksModal" @close="closeHowItWorksModal" />
</template>

<script>
import axios from "axios";
import { reactive } from "vue";
import RaffleTransactionModal from "@/components/RaffleTransactionModal.vue";
import RedirectModal from "@/components/RedirectModal.vue";
import HowRafflesWorkModal from "@/components/HowRafflesWorkModal.vue";

export default {
  data() {
    return {
      loading: false,
      points: 0,
      animatedPoints: 0,
      animatedTotalRaffles: 0,
      searchQuery: "",
      showDesktopFilter: false,
      showMobileFilter: false,
      showTransactionModal: false,
      raffles: [],
      activeRaffles: [],
      endedRaffles: [],
      analytics: {
        totalPointsSpent: null,
        totalPurchases: null,
        popularRaffle: null,
        topSpender: null,
      },
      showSortDropdown: false,
      selectedSortOption: { label: this.$t('Store.Sort.lowToHigh'), value: "lowToHigh" },
      sortOptions: [
        { label: this.$t('Store.Sort.highToLow'), value: "highToLow" },
        { label: this.$t('Store.Sort.lowToHigh'), value: "lowToHigh" },
        { label: this.$t('Store.Sort.aToZ'), value: "aToZ" },
        { label: this.$t('Store.Sort.zToA'), value: "zToA" },
      ],
      selectedPointsRange: "",
      selectedCategory: "All",
      minPoints: "",
      maxPoints: "",
      currentPage: 1,
      itemsPerPage: 8,
      showErrorModal: false,
      errorMessage: "",
      showSuccessModal: true,
      showConfirmModal: false,
      showRedirectModal: false,
      showHowItWorksModal: false,
      selectedRaffleId: null,
      selectedRaffleName: "",
      selectedRafflePoints: 0,
      currentActivePage: 1,
      currentInactivePage: 1,
      transactions: [],
      countdowns: reactive({}),
      countdownInterval: null,
      selectedRaffle: null,
      ticket: null,
    };
  },
  components: {
    RedirectModal,
    RaffleTransactionModal,
    HowRafflesWorkModal,
  },
  computed: {
    filteredActiveRaffles() {
      return this.applyFiltersAndSorting(this.activeRaffles);
    },
    activePaginatedRaffles() {
      const start = (this.currentActivePage - 1) * this.itemsPerPage;
      return this.filteredActiveRaffles.slice(start, start + this.itemsPerPage);
    },
    activeTotalPages() {
      return Math.ceil(this.filteredActiveRaffles.length / this.itemsPerPage);
    },
    activeTotalItems() {
      return this.filteredActiveRaffles.length;
    },

    // Filtered Inactive Raffles with Search & Sorting
    filteredInactiveRaffles() {
      return this.applyFiltersAndSorting(this.endedRaffles);
    },
    inactivePaginatedRaffles() {
      const start = (this.currentInactivePage - 1) * this.itemsPerPage;
      return this.filteredInactiveRaffles.slice(start, start + this.itemsPerPage);
    },
    inactiveTotalPages() {
      return Math.ceil(this.filteredInactiveRaffles.length / this.itemsPerPage);
    },
    inactiveTotalItems() {
      return this.filteredInactiveRaffles.length;
    },
    totalRaffles() {
      return this.activeRaffles.length + this.endedRaffles.length;
    },
    isUserLoggedIn() {
      return this.$store.state.user !== null && this.$store.state.isVerified;
    },
    needsProfileUpdate() {
      const user = this.$store.state.user;
      return user && (!user.stake || !user.kick);
    },
  },
  methods: {
    async init() {
      const baseUrl = process.env.VUE_APP_BASE_API;
      this.loading = true;

      try {
        const token = localStorage.getItem("maherco-access-token");

        if (!token) {
          console.log("No access token found. Showing redirect modal.");
          this.showRedirectModal = true;
          return;
        }

        const userResponse = await axios.get(`${baseUrl}/api/users/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-api-key": process.env.VUE_APP_X_API_KEY,
          },
        });

        const user = userResponse.data?.user;

        if (user) {
          this.$store.commit("UPDATE_USER", user);
          this.$store.commit("UPDATE_ISVERIFIED", true);

          if (!this.isUserLoggedIn) {
            this.showRedirectModal = true;
          }
        } else {
          this.showRedirectModal = true;
        }
      } catch (error) {
        console.error("Error verifying user. Showing redirect modal.");
        this.$store.commit("UPDATE_USER", null);
        this.$store.commit("UPDATE_ISVERIFIED", false);
        this.showRedirectModal = true;
      } finally {
        this.loading = false;
      }
    },
    async fetchRaffles() {
      this.loading = true;
      const baseUrl = process.env.VUE_APP_BASE_API;

      try {
        const response = await axios.get(`${baseUrl}/api/raffles`, {
          headers: {
            "x-api-key": process.env.VUE_APP_X_API_KEY,
          },
        });

        const raffles = response.data.raffles || [];

        this.activeRaffles = raffles.filter(
          (raffle) =>
            (raffle.status === "started" || raffle.status === "stopped") &&
            raffle.timeStarted !== null
        );

        this.endedRaffles = raffles.filter((raffle) => raffle.status === "ended");
      } catch (error) {
        console.error("Error fetching raffles:", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchTransactions() {
      const baseUrl = process.env.VUE_APP_BASE_API;
      const token = localStorage.getItem("maherco-access-token");

      if (!token) {
        this.showError("User not authenticated.");
        return;
      }

      try {
        const response = await axios.get(`${baseUrl}/api/raffles/purchase/transactions`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-api-key": process.env.VUE_APP_X_API_KEY,
          },
        });

        if (response.status === 200) {
          this.transactions = Array.isArray(response.data.purchaseTransactions)
            ? response.data.purchaseTransactions.sort(
                (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
              )
            : [];
          this.showTransactionModal = true;
        }
      } catch (error) {
        console.error("Error fetching transactions:", error);
      }
    },
    async fetchUserTotalEntries() {
      const baseUrl = process.env.VUE_APP_BASE_API;
      const token = localStorage.getItem("accessToken");

      if (!token || !this.selectedRaffle) return;

      try {
        const response = await axios.get(
          `${baseUrl}/api/raffles/${this.selectedRaffle._id}/user-entries`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-api-key": process.env.VUE_APP_X_API_KEY,
            },
          }
        );

        this.totalEntries = response.data.totalEntries || 0; // Store total entries
      } catch (error) {
        console.error("Error fetching total entries:", error);
      }
    },
    async fetchUserPoints() {
      const baseUrl = process.env.VUE_APP_BASE_API;
      const token = localStorage.getItem("maherco-access-token");

      if (!token) {
        this.showError("You must be logged in to view your points.", "Login Required");
        return;
      }

      try {
        const response = await axios.get(`${baseUrl}/api/users/me/points`, {
          headers: {
            "x-api-key": process.env.VUE_APP_X_API_KEY,
            Authorization: `Bearer ${token}`,
          },
        });

        this.points = response.data.points;

        this.animateValues();
      } catch (error) {
        console.error("Error fetching user points:", error);
      }
    },
    async fetchRaffleTransactions() {
      const baseUrl = process.env.VUE_APP_BASE_API;
      const token = localStorage.getItem("maherco-access-token");

      if (!token) {
        console.error("User not authenticated.");
        return;
      }

      try {
        const response = await axios.get(`${baseUrl}/api/raffles/purchase/transactions`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-api-key": process.env.VUE_APP_X_API_KEY,
          },
        });

        if (response.status === 200 && response.data.purchaseTransactions) {
          this.transactions = response.data.purchaseTransactions.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
        } else {
          this.transactions = [];
        }
      } catch (error) {
        console.error("Error fetching raffle transactions:", error);
      }
    },
    updateCountdowns() {
      this.activeRaffles.forEach((raffle) => {
        this.countdowns[raffle.id] = this.formatCountdown(raffle.deadline);
      });
    },

    formatCountdown(endTime) {
      if (!endTime) return "Invalid Date";

      const now = new Date().getTime();
      const end = new Date(endTime).getTime();

      if (isNaN(end)) return "Invalid Date";

      const timeDiff = end - now;

      if (timeDiff <= 0) return "Ended";

      const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

      return `${days}D ${hours}H ${minutes}M ${seconds}S`;
    },
    startCountdowns() {
      this.activeRaffles.forEach((raffle) => {
        if (raffle.status !== "stopped") {
          this.countdowns[raffle._id] = this.calculateCountdown(raffle);
        }
      });

      this.countdownInterval = setInterval(() => {
        this.activeRaffles.forEach((raffle) => {
          if (raffle.status !== "stopped") {
            this.countdowns[raffle._id] = this.calculateCountdown(raffle);
          }
        });
      }, 1000);
    },
    calculateCountdown(raffle) {
      if (raffle.status === "stopped") return ""; // Don't display countdown for stopped raffles

      if (!raffle.timeStarted || !raffle.deadline) return "Invalid Date";

      const now = new Date().getTime();
      const start = new Date(raffle.timeStarted).getTime();
      const end = new Date(raffle.deadline).getTime();

      if (isNaN(start) || isNaN(end)) return "Invalid Date";

      if (now < start) return "Not Started";

      const timeDiff = end - now;
      if (timeDiff <= 0) return "Ended";

      const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

      return `${days}D ${hours}H ${minutes}M ${seconds}S`;
    },
    animateValues() {
      this.animateNumber("animatedPoints", this.points);
      this.animateNumber(
        "animatedTotalRaffles",
        this.activeRaffles.length + this.endedRaffles.length
      );
    },
    animateNumber(variable, targetValue) {
      let startValue = this[variable] || 0;
      if (startValue === targetValue) return;

      const step = Math.max(1, Math.abs(targetValue - startValue) / 20); // Ensures step is at least 1
      const isNegative = startValue > targetValue;

      const interval = setInterval(() => {
        if (isNegative) {
          startValue -= step;
          if (startValue <= targetValue) {
            startValue = targetValue;
            clearInterval(interval);
          }
        } else {
          startValue += step;
          if (startValue >= targetValue) {
            startValue = targetValue;
            clearInterval(interval);
          }
        }

        // Ensure whole numbers with commas
        this[variable] = Math.round(startValue).toLocaleString();
      }, 50);
    },
    applyFiltersAndSorting(raffles) {
      let filtered = raffles;

      // ✅ Consistent title field
      if (this.searchQuery.trim() !== "") {
        filtered = filtered.filter((raffle) =>
          raffle.name?.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }

      if (this.minPoints !== "" || this.maxPoints !== "") {
        const min = this.minPoints !== "" ? Number(this.minPoints) : 0;
        const max = this.maxPoints !== "" ? Number(this.maxPoints) : Infinity;

        filtered = filtered.filter(
          (raffle) => raffle.entryCost >= min && raffle.entryCost <= max
        );
      }

      switch (this.selectedSortOption.value) {
        case "lowToHigh":
          filtered.sort((a, b) => a.entryCost - b.entryCost);
          break;
        case "highToLow":
          filtered.sort((a, b) => b.entryCost - a.entryCost);
          break;
        case "aToZ":
          filtered.sort((a, b) => a.name?.localeCompare(b.name));
          break;
        case "zToA":
          filtered.sort((a, b) => b.name?.localeCompare(a.name));
          break;
      }

      return filtered;
    },
    refreshPage() {
      this.fetchRaffles();
      this.fetchUserPoints();
    },
    applySort(option) {
      this.selectedSortOption = option;
      this.showSortDropdown = false;
    },
    toggleSortDropdown() {
      this.showSortDropdown = !this.showSortDropdown;
    },
    openTransactionModal() {
      this.showTransactionModal = true;
      this.fetchRaffleTransactions();
    },
    goToActivePage(page) {
      this.currentActivePage = page;
    },
    prevActivePage() {
      if (this.currentActivePage > 1) this.currentActivePage--;
    },
    nextActivePage() {
      if (this.currentActivePage < this.activeTotalPages) this.currentActivePage++;
    },

    goToInactivePage(page) {
      this.currentInactivePage = page;
    },
    prevInactivePage() {
      if (this.currentInactivePage > 1) this.currentInactivePage--;
    },
    nextInactivePage() {
      if (this.currentInactivePage < this.inactiveTotalPages) this.currentInactivePage++;
    },
    goToRaffle(id) {
      this.$router.push(`/raffle/${id}`);
    },
    closeHowItWorksModal() {
      this.showHowItWorksModal = false;
    },
  },
  mounted() {
    this.init();
    this.fetchUserPoints();
    this.fetchRaffles().then(() => {
      this.startCountdowns();
    });
  },
  beforeUnmount() {
    clearInterval(this.countdownInterval);
  },
};
</script>

<style>
/* Smooth fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.loader {
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 3px solid white;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes twist-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.twist-spinner {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: 3px solid rgba(46, 187, 240, 0.15);
  border-top-color: #00F0FF;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

.twist-spinner-small {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 2px solid rgba(46, 187, 240, 0.15);
  border-top-color: #00F0FF;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

.twist-spinner-big {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 4px solid rgba(46, 187, 240, 0.15);
  border-top-color: #00F0FF;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

</style>
